import type { ComponentPropsWithRef, MouseEvent, ReactElement, ReactNode } from 'react';
import { buttonClassName } from './buttonStyles';
import type { ButtonSize, ButtonVariant } from './buttonStyles';
import { Spinner } from './Spinner';

export type { ButtonSize, ButtonVariant } from './buttonStyles';

export interface ButtonProps extends ComponentPropsWithRef<'button'> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  loading?: boolean;
  iconStart?: ReactNode;
  iconEnd?: ReactNode;
  fullWidth?: boolean;
}

/**
 * The disabled state is expressed with `aria-disabled` rather than the native
 * attribute: a disabled toolbar control still needs to be reachable so its
 * tooltip can explain *why* it is unavailable. Clicks (and therefore implicit
 * form submission) are blocked in the handler instead.
 */
export function Button({
  variant = 'secondary',
  size = 'md',
  loading = false,
  iconStart,
  iconEnd,
  fullWidth = false,
  disabled = false,
  className,
  children,
  onClick,
  type = 'button',
  ...rest
}: ButtonProps): ReactElement {
  const inert = disabled || loading;

  function handleClick(event: MouseEvent<HTMLButtonElement>): void {
    if (inert) {
      event.preventDefault();
      event.stopPropagation();
      return;
    }
    onClick?.(event);
  }

  return (
    <button
      {...rest}
      type={type}
      onClick={handleClick}
      aria-disabled={inert || undefined}
      aria-busy={loading || undefined}
      data-loading={loading ? '' : undefined}
      className={buttonClassName({ variant, size, fullWidth, disabled: inert, className })}
    >
      {loading ? (
        <Spinner size="sm" label={null} />
      ) : (
        iconStart && (
          <span aria-hidden="true" className="flex shrink-0 items-center [&>svg]:h-4 [&>svg]:w-4">
            {iconStart}
          </span>
        )
      )}
      {children != null && children !== false && <span className="truncate">{children}</span>}
      {iconEnd && (
        <span aria-hidden="true" className="flex shrink-0 items-center [&>svg]:h-4 [&>svg]:w-4">
          {iconEnd}
        </span>
      )}
    </button>
  );
}
